import type { ProductListItem } from 'product-list/types/ProductsList'
import type { ReactNode } from 'react'

import { ModuleVisibilityGuard } from '../components/ModuleVisibilityGuard/ModuleVisibilityGuard'
import { M1 } from '../components/modules/M1/M1'
import { M2 } from '../components/modules/M2/M2'
import { M3 } from '../components/modules/M3/M3'
import { M4 } from '../components/modules/M4/M4'
import { M5 } from '../components/modules/M5/M5'
import { M6 } from '../components/modules/M6/M6'
import { RenderingType } from '../types/InspirationalProductCard'
import { type Module, ModuleTag } from '../types/modules'
import type { ProductInfo } from '../types/product'
import { renderProduct, renderProducts } from './product'

type RenderModuleParams = {
	module: Module
	index: number
	type: RenderingType
	shouldRenderSlideshowAnimation: boolean
}

const resolveModule = ({
	module,
	index,
	type,
	shouldRenderSlideshowAnimation,
}: RenderModuleParams): ReactNode => {
	const key = `module-${index}`
	switch (module.tag) {
		case ModuleTag.M1:
			return (
				<M1 key={key}>
					{renderProduct({
						type,
						shouldRenderSlideshowAnimation,
						moduleIndex: index,
						productIndex: 0,
						product: module.product,
					})}
				</M1>
			)
		case ModuleTag.M2:
			return (
				<M2 key={key}>
					{renderProducts({
						type,
						shouldRenderSlideshowAnimation,
						products: module.products,
						moduleIndex: index,
					})}
				</M2>
			)
		case ModuleTag.M3:
			return (
				<M3 key={key}>
					{renderProducts({
						type,
						products: module.products,
						moduleIndex: index,
					})}
				</M3>
			)
		case ModuleTag.M4:
			return (
				<M4 key={key}>
					{renderProducts({
						type,
						products: module.products,
						moduleIndex: index,
					})}
				</M4>
			)
		case ModuleTag.M5:
			return (
				<M5 key={key}>
					{renderProduct({
						type,
						moduleIndex: index,
						productIndex: 0,
						product: module.product,
						isTwoColumnSize: true,
					})}
				</M5>
			)
		case ModuleTag.M6:
			return (
				<M6 key={key}>
					{renderProducts({
						type,
						shouldRenderSlideshowAnimation,
						products: module.products,
						moduleIndex: index,
					})}
				</M6>
			)
		default:
			throw new Error(`Unknown module type ${module.tag}`)
	}
}

export const renderModule = (params: RenderModuleParams) => {
	const resolvedModule = resolveModule(params)
	return (
		<ModuleVisibilityGuard index={params.index}>
			{resolvedModule}
		</ModuleVisibilityGuard>
	)
}

export const getRenderingType = (index: number, nServerModules: number) =>
	index < nServerModules ? RenderingType.Server : RenderingType.Client

export const getSlideshowAnimationModuleIndex = (modules: Module[]) => {
	const slideshowModuleTags = [ModuleTag.M1, ModuleTag.M2, ModuleTag.M6]
	return modules.findIndex((module) => slideshowModuleTags.includes(module.tag))
}

export const productMatchesProductInfo = (
	product: ProductListItem,
	productInfo: ProductInfo
) => {
	return (
		product.id === productInfo.id && product.colorId === productInfo.colorId
	)
}

export const getLastVisitedProductModuleIndex = (
	modules: Module[],
	lastProductInfo: ProductInfo
) => {
	return modules.findIndex((module) => {
		if (module.tag === ModuleTag.M1 || module.tag === ModuleTag.M5) {
			return productMatchesProductInfo(module.product, lastProductInfo)
		}
		if (
			module.tag === ModuleTag.M2 ||
			module.tag === ModuleTag.M3 ||
			module.tag === ModuleTag.M4 ||
			module.tag === ModuleTag.M6
		) {
			return module.products.some((product) =>
				productMatchesProductInfo(product, lastProductInfo)
			)
		}
		return false
	})
}
